import { OwnerState, OwnerSocial } from "@/types/owner-edit";
import { OWNER_EDIT_CONSTANTS } from "@/constants/owner-edit";

export const ownerEditUtils = {
  /**
   * Generate a default username from wallet address
   */
  generateDefaultUsername: (address: string): string => {
    const prefix = address.slice(0, 4);
    const suffix = address.slice(-4);
    return `USER#${prefix}...${suffix}`;
  },

  /**
   * Create object URL for file preview
   */
  createFilePreviewUrl: (file: File | string): string => {
    if (typeof file === "string") {
      return file;
    }
    return URL.createObjectURL(file);
  },

  /**
   * Clean up object URL
   */
  revokeFilePreviewUrl: (url: string): void => {
    URL.revokeObjectURL(url);
  },

  /**
   * Update a specific social link
   */
  updateSocialLink: (
    socials: OwnerSocial[],
    index: number,
    value: string
  ): OwnerSocial[] => {
    return socials.map((social, i) =>
      i === index ? { ...social, link: value } : social
    );
  },

  /**
   * Update a specific stat visibility
   */
  updateStatVisibility: (
    stats: OwnerState["stats"],
    index: number
  ): OwnerState["stats"] => {
    return stats.map((stat, i) =>
      i === index ? { ...stat, isVisible: !stat.isVisible } : stat
    );
  },

  /**
   * Check if avatar is the default avatar
   */
  isDefaultAvatar: (avatarUrl: string): boolean => {
    return avatarUrl === OWNER_EDIT_CONSTANTS.DEFAULT_AVATAR;
  },

  /**
   * Check if background is the default background
   */
  isDefaultBackground: (backgroundUrl: string): boolean => {
    return backgroundUrl === OWNER_EDIT_CONSTANTS.DEFAULT_BACKGROUND;
  },

  /**
   * Get social links that have values
   */
  getActiveSocialLinks: (socials: OwnerSocial[]): OwnerSocial[] => {
    return socials.filter(social => social.link && social.link.trim() !== "");
  },

  /**
   * Check if any social links are present
   */
  hasSocialLinks: (socials: OwnerSocial[]): boolean => {
    return socials.some(social => social.link && social.link.trim() !== "");
  },

  /**
   * Truncate text to specified length
   */
  truncateText: (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  },

  /**
   * Format file size in human readable format
   */
  formatFileSize: (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  },

  /**
   * Check if file size is within limits
   */
  isFileSizeValid: (file: File, maxSizeMB: number): boolean => {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    return file.size <= maxSizeBytes;
  },

  /**
   * Check if image dimensions are within limits
   */
  checkImageDimensions: (
    file: File,
    maxWidth: number,
    maxHeight: number
  ): Promise<boolean> => {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => {
        resolve(img.width <= maxWidth && img.height <= maxHeight);
      };
      img.onerror = () => resolve(false);
      img.src = URL.createObjectURL(file);
    });
  },

  /**
   * Get file extension
   */
  getFileExtension: (filename: string): string => {
    return filename.split(".").pop()?.toLowerCase() || "";
  },

  /**
   * Check if file is an image
   */
  isImageFile: (file: File): boolean => {
    return file.type.startsWith("image/");
  },

  /**
   * Deep clone object
   */
  deepClone: <T>(obj: T): T => {
    return JSON.parse(JSON.stringify(obj));
  },
};
